<template>
  <div>
    <v-card class="pa-4 my-4" outlined>
      <v-row class="ma-0" align="center">
        <v-col cols="12" md="5">
          <v-text-field
            v-model="searchText"
            prepend-inner-icon="mdi-magnify"
            placeholder="Buscar por nombre"
            outlined
            dense
            hide-details
            clearable
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :search="searchText"
      :items-per-page="10"
      sort-by="name"
      class="elevation-1"
      no-data-text="No hay tipos de adjunto registrados"
    >
      <template v-slot:item.name="{ item }">
        <span class="font-weight-medium">{{ item.name }}</span>
      </template>

      <template v-slot:item.description="{ item }">
        <span class="grey--text text--darken-2">{{ item.description || '—' }}</span>
      </template>

      <template v-slot:item.template_url="{ item }">
        <a v-if="item.template_url" :href="item.template_url" target="_blank">Ver plantilla</a>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.required="{ item }">
        <v-icon :color="item.required ? 'green' : 'grey'">
          {{ item.required ? 'mdi-check-circle' : 'mdi-close-circle' }}
        </v-icon>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center justify-end flex-nowrap">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="mx-2"
                fab
                dark
                depressed
                x-small
                v-bind="attrs"
                v-on="on"
                color="orange"
                @click="openMaintainerDialog(item)"
              >
                <v-icon small dark>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>Editar</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="mx-2"
                fab
                dark
                depressed
                x-small
                v-bind="attrs"
                v-on="on"
                color="pink"
                @click="confirmRemove(item)"
              >
                <v-icon small dark>mdi-delete-forever</v-icon>
              </v-btn>
            </template>
            <span>Eliminar</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <!-- Create / edit dialog -->
    <v-dialog v-model="maintainerDialog" min-width="500" max-width="700" @keydown.esc="closeMaintainerDialog">
      <attachment-type-maintainer
        v-if="maintainerDialog"
        :attachment-type="selectedItem"
        @update-list="onSaved"
        @close-dialog="closeMaintainerDialog"
      ></attachment-type-maintainer>
    </v-dialog>

    <!-- Confirm delete dialog -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card class="pa-6">
        <v-card-title class="text-uppercase">
          <div>Eliminar tipo {{ itemToDelete != null ? itemToDelete.name : '' }}</div>
        </v-card-title>
        <hr class="my-3" />
        <v-card-text class="my-0 py-0">
          <p>¿Está seguro de que desea eliminar este tipo de adjunto?</p>
        </v-card-text>
        <hr class="my-3" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="closeDeleteDialog">Cancelar</v-btn>
          <v-btn color="primary" rounded :loading="deleting" @click="removeItem">Sí, eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="5000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import AttachmentTypesService from '../services/attachment-types.service';
import AttachmentTypeMaintainer from './AttachmentTypeMaintainer.vue';
import { utilsMixin } from '../mixins/utilsMixin';

export default {
  name: 'AttachmentTypesTab',
  components: { AttachmentTypeMaintainer },
  mixins: [utilsMixin],
  data() {
    return {
      loading: false,
      deleting: false,
      items: [],
      searchText: '',
      maintainerDialog: false,
      selectedItem: null,
      deleteDialog: false,
      itemToDelete: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'red',
      headers: [
        { text: 'Nombre', value: 'name', sortable: true, width: '220px' },
        { text: 'Descripción', value: 'description', sortable: false },
        { text: 'Plantilla', value: 'template_url', sortable: false, width: '160px' },
        { text: 'Obligatorio', value: 'required', sortable: true, width: '120px', align: 'center' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'end' },
      ],
    };
  },
  mounted() {
    this.loadItems();
  },
  methods: {
    async loadItems() {
      this.loading = true;
      const res = await AttachmentTypesService.getAll();
      if (res.status === 'ok') this.items = res.data;
      else this.showErrorSnackbar('No se pudieron cargar los tipos de adjunto.');
      this.loading = false;
    },

    onSaved() {
      this.showSuccessSnackbar('Tipo de adjunto guardado correctamente');
      this.loadItems();
    },

    // Llamado desde el botón "+" compartido en Servicios.vue vía $refs cuando
    // el tab activo es "Tipos de Adjunto".
    openMaintainerDialog(item) {
      this.selectedItem = item || null;
      this.maintainerDialog = true;
    },

    closeMaintainerDialog() {
      this.maintainerDialog = false;
      this.selectedItem = null;
    },

    confirmRemove(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    closeDeleteDialog() {
      this.deleteDialog = false;
      this.itemToDelete = null;
    },

    async removeItem() {
      this.deleting = true;
      const res = await AttachmentTypesService.remove(this.itemToDelete.id);
      if (res.status === 'ok') {
        this.showSuccessSnackbar('Tipo de adjunto eliminado correctamente');
        this.items = this.items.filter(i => i.id !== this.itemToDelete.id);
        this.closeDeleteDialog();
      } else {
        // 409: el backend ya envía el motivo (está en uso) — se muestra tal cual
        this.showErrorSnackbar(res.message || 'No se pudo eliminar el tipo de adjunto.');
      }
      this.deleting = false;
    },
  },
};
</script>
